//@ts-nocheck
import CircularProgress from '@mui/material/CircularProgress';
import { Box } from '@mui/material';
import { XGButton, XGModal } from '@xg-cl/xg-cl';
import { useTranslation } from 'react-i18next';
import { formatCurrency } from '../../../../../utils/currencyHelper';
import * as PropTypes from 'prop-types';

/**
 * Functional React component for rendering a custom update modal component.
 *
 * @namespace Components
 *
 * @param {Object} props - The component's properties
 * @param {boolean} props.isOpen - Boolean value to indicate whether modal is open.
 * @param {boolean} props.isSubmitting - Boolean value to indicate whether form is submitting.
 * @param {Object} props.updatedBookingPlan - The updated booking plan with passengers.
 * @param {Object} props.bookingData - The booking data.
 * @param {Function} [props.onClose] - Callback function to handle form closing.
 * @param {Function} [props.onSubmit] - Callback function to handle form submission.
 * @param {...any} props.rest - Additional props to be spread on the parent element.
 *
 * @returns {JSX.Element} React element representing the update modal component.
 */
const ConfirmUpdateModal = ({
  isOpen,
  isSubmitting,
  updatedBookingPlan,
  bookingData,
  onClose = () => {},
  onSubmit = () => {},
  ...rest
}) => {
  const { t } = useTranslation(['bookings', 'common']);
  const passengers = updatedBookingPlan?.passengers || [];
  const oldTripPlan = updatedBookingPlan?.oldTripPlan || null;
  const newTripPlan = updatedBookingPlan?.newTripPlan || null;
  const bookingPlans = bookingData?.bookingplans || [];
  const foundBookingPlan = bookingPlans?.find(
    (bookingPlan) =>
      bookingPlan?.bookingplanid === updatedBookingPlan?.bookingPlanId
  );
  const previousPlanTotal =
    Number(foundBookingPlan?.priceperperson || 0) * passengers?.length;
  const newPlanTotal =
    Number(newTripPlan?.priceperperson || 0) * passengers?.length;
  const newBookingTotal =
    Number(bookingData?.bookingtotal || 0) + newPlanTotal - previousPlanTotal;

  return (
    <XGModal
      allowClosing={true}
      data-testid={'update-passenger-modal'}
      className='booking-panel__modal'
      actions={
        <Box
          className='booking-panel__modal-actions'
          data-testid='booking-panel__modal-actions'
        >
          <XGButton
            data-testid='update-action-confirm-button'
            id='update-action-confirm-button'
            text={t('common:cancel')}
            type='secondary'
            size='large'
            variant='outlined'
            onClick={onClose}
            disabled={isSubmitting}
            style={{ width: 'auto' }}
          />
          <Box sx={{ position: 'relative' }}>
            <XGButton
              style={{
                width: 'auto'
              }}
              className='w-auto '
              data-testid='update-action-cancel-button'
              id='update-action-cancel-button'
              text={t('bookings:confirmChange')}
              type='primary'
              size='large'
              variant='contained'
              onClick={onSubmit}
              disabled={isSubmitting}
            />
            {isSubmitting && (
              <CircularProgress
                id='update-actions-loader'
                size={24}
                className='spinner spinner__primary500'
              />
            )}
          </Box>
        </Box>
      }
      dismissable={false}
      open={isOpen}
      title={
        <span className='booking-panel__modal-pricing-plan__title'>
          {t('bookings:confirmPlanChange')}
        </span>
      }
      labelledby={t('bookings:confirmPlanChange')}
      maxWidth='lg'
      scroll='body'
      onClose={onClose}
      {...rest}
    >
      <Box className='booking-panel__modal__content'>
        <Box className='booking-panel__modal__total-row'>
          <span className='booking-panel__modal__total-title'>
            {t('bookings:previousBookingTotal')}
          </span>
          <span className='booking-panel__modal__total-title'>
            {formatCurrency(bookingData?.bookingtotal || 0)}
          </span>
        </Box>
        <Box className='booking-panel__modal-pricing-plan_wrapper'>
          <span className='booking-panel__modal__muted-text'>
            {t('bookings:currentPlan')}
          </span>
          <Box className='booking-panel__modal-pricing-plan_bordered-container'>
            <Box className='booking-panel__modal__total-row'>
              <span className='booking-panel__modal__total-title'>
                {oldTripPlan?.title}
              </span>
            </Box>

            {passengers?.map((passenger) => {
              const { passengerid, fullname } = passenger;
              return (
                <Box
                  className='booking-panel__modal__total-row ml-4'
                  key={passengerid}
                >
                  <span className='booking-panel__modal__subitem-title'>
                    {fullname}
                  </span>
                  <span className='booking-panel__modal__subitem-title'>
                    {formatCurrency(foundBookingPlan?.priceperperson || 0)}
                  </span>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box className='booking-panel__modal-pricing-plan_wrapper'>
          <span className='booking-panel__modal__muted-text'>
            {t('bookings:newPlan')}
          </span>
          <Box className='booking-panel__modal-pricing-plan_bordered-container'>
            <Box className='booking-panel__modal__total-row'>
              <span className='booking-panel__modal__total-title'>
                {newTripPlan?.title}
              </span>
            </Box>

            {passengers?.map((passenger) => {
              const { passengerid, fullname } = passenger;
              return (
                <Box
                  className='booking-panel__modal__total-row ml-4'
                  key={passengerid}
                >
                  <span className='booking-panel__modal__subitem-title'>
                    {fullname}
                  </span>
                  <span className='booking-panel__modal__subitem-title'>
                    {formatCurrency(newTripPlan?.priceperperson || 0)}
                  </span>
                </Box>
              );
            })}
          </Box>
        </Box>
        <Box className='booking-panel__modal__total-row'>
          <span className='booking-panel__modal__total-title'>
            {t('bookings:newBookingTotal')}
          </span>
          <span className='booking-panel__modal__total-title'>
            {formatCurrency(newBookingTotal || 0)}
          </span>
        </Box>
      </Box>
    </XGModal>
  );
};

ConfirmUpdateModal.propTypes = {
  updatedBookingPlan: PropTypes.object,
  bookingData: PropTypes.object,
  isOpen: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  onClose: PropTypes.func,
  onSubmit: PropTypes.func
};

export default ConfirmUpdateModal;
